import { useState, useEffect } from 'react';
import { useQuery, ApolloError } from '@apollo/client';
import { GET_COLLECTION_PRODUCTS, GET_COLLECTION_PRODUCTS_INTERMEDIATE, GET_COLLECTION_PRODUCTS_LIGHTWEIGHT, GET_ALL_PRODUCTS } from '../graphql/queries';
import type { ProductOption } from '../types/productTypes';
import { convertToProductOptions } from '../utils/shopifyDataTransformers';
import type { ShopifyProduct, ShopifyOption } from '../utils/shopifyDataTransformers';
import type { GraphQLCollectionProductsData, GraphQLAllProductsData, GraphQLProductEdge } from '../types/graphqlTypes';
import { debug, error } from '../utils/debug';

interface UseStagedProductsResult {
  // Stage 1: Initial limited products for immediate display
  initialProducts: ProductOption[];
  initialShopifyProducts: ShopifyProduct[];
  isInitialLoading: boolean;
  
  // Stage 2: All products with 1 variant each for filtering
  intermediateProducts: ProductOption[];
  intermediateShopifyProducts: ShopifyProduct[];
  isIntermediateLoading: boolean;
  
  // Stage 3: All products with full variant data
  allProducts: ProductOption[];
  allShopifyProducts: ShopifyProduct[];
  isAllProductsLoading: boolean;
  
  error: ApolloError | undefined;
}

/**
 * AIDEV-NOTE: Three-Stage Product Loading Hook
 * 
 * This hook implements a three-stage loading strategy for optimal performance:
 * 1. Stage 1: Load first N products with minimal data for instant display
 * 2. Stage 2: Load all products with 1 variant each for filtering/searching
 * 3. Stage 3: Load all products with full variant data for storage options
 * 
 * Benefits:
 * - Ultra-fast initial render (minimal data)
 * - Quick filtering/search capability (intermediate data)
 * - Full functionality when all data is loaded (complete data)
 * - Progressive enhancement without blocking user interaction
 */
export function useProductsWithStagedLoading(
  collectionHandle: string = 'all-products-oos-for-zarak',
  initialProductCount: number = 10
): UseStagedProductsResult {
  // Stage 1: Initial products state
  const [initialProducts, setInitialProducts] = useState<ProductOption[]>([]);
  const [initialShopifyProducts, setInitialShopifyProducts] = useState<ShopifyProduct[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  // Stage 2: Intermediate products state (1 variant each)
  const [intermediateProducts, setIntermediateProducts] = useState<ProductOption[]>([]);
  const [intermediateShopifyProducts, setIntermediateShopifyProducts] = useState<ShopifyProduct[]>([]);
  
  // Stage 3: All products state (full variant data)
  const [allProducts, setAllProducts] = useState<ProductOption[]>([]);
  const [allShopifyProducts, setAllShopifyProducts] = useState<ShopifyProduct[]>([]);

  // Stage 1: Fetch limited products first (using lightweight query)
  const {
    error: initialError,
    data: initialData
  } = useQuery<GraphQLCollectionProductsData>(
    GET_COLLECTION_PRODUCTS_LIGHTWEIGHT,
    {
      variables: { handle: collectionHandle, first: initialProductCount },
      onError: (err) => {
        error('Initial products query failed:', err);
      },
      fetchPolicy: 'cache-first' // Use cache for faster subsequent loads
    }
  );

  // Stage 2: Fetch all products with 1 variant each (delayed start)
  const {
    loading: intermediateLoading,
    error: intermediateError,
    data: intermediateData
  } = useQuery<GraphQLCollectionProductsData>(
    GET_COLLECTION_PRODUCTS_INTERMEDIATE,
    {
      variables: { handle: collectionHandle, first: 100 },
      skip: isInitialLoading, // Don't start until initial products are loaded
      onError: (err) => {
        error('Intermediate products query failed:', err);
      },
      fetchPolicy: 'cache-first'
    }
  );

  // Stage 3: Fetch all products with full variant data (delayed start)
  const {
    loading: allProductsLoading,
    error: allProductsError,
    data: allProductsData
  } = useQuery<GraphQLCollectionProductsData>(
    GET_COLLECTION_PRODUCTS,
    {
      variables: { handle: collectionHandle, first: 100 },
      skip: intermediateLoading || intermediateProducts.length === 0, // Don't start until intermediate products are loaded
      onError: (err) => {
        error('All products query failed:', err);
      },
      fetchPolicy: 'cache-first' // Use cache for full product data
    }
  );

  // Fallback query if collection fails
  const {
    loading: fallbackLoading,
    error: fallbackError,
    data: fallbackData
  } = useQuery<GraphQLAllProductsData>(
    GET_ALL_PRODUCTS,
    {
      variables: { first: 100 },
      skip: !initialError && !allProductsError, // Only run if both collection queries fail
      onError: (err) => {
        error('Fallback all products query failed:', err);
      }
    }
  );

  // Helper function to process GraphQL edges to ShopifyProduct[]
  const processProducts = (edges: GraphQLProductEdge[]): ShopifyProduct[] => {
    if (!edges || edges.length === 0) return [];
    
    return edges.map((edge) => {
      const node = edge.node;
      
      const product: ShopifyProduct = {
        id: node.id,
        handle: node.handle,
        title: node.title,
        vendor: node.vendor,
        productType: node.productType,
        tags: node.tags || [],
        variants: node.variants,
        options: node.options as unknown as ShopifyOption[],
        images: node.images,
        price: node.variants?.edges[0]?.node.price?.amount || '0',
        metafields: node.metafields
      };
      
      return product;
    });
  };

  // Process Stage 1: Initial products
  useEffect(() => {
    if (initialData?.collection?.products?.edges && isInitialLoading) {
      const products = processProducts(initialData.collection.products.edges);
      
      if (products.length > 0) {
        debug(`STAGE 1: Setting ${products.length} initial products for immediate display`);
        const productOptions = convertToProductOptions(products);
        
        setInitialProducts(productOptions);
        setInitialShopifyProducts(products);
        setIsInitialLoading(false);
        debug(`STAGE 1: Initial loading complete, triggering stage 2`);
      }
    }
  }, [initialData, isInitialLoading]);

  // Process Stage 2: Intermediate products (1 variant each)
  useEffect(() => {
    if (intermediateData?.collection?.products?.edges) {
      const products = processProducts(intermediateData.collection.products.edges);
      
      if (products.length > 0) {
        debug(`STAGE 2: Setting ${products.length} intermediate products for filtering`);
        const productOptions = convertToProductOptions(products);
        
        setIntermediateProducts(productOptions);
        setIntermediateShopifyProducts(products);
        debug(`STAGE 2: Intermediate loading complete, triggering stage 3`);
      }
    }
  }, [intermediateData]);

  // Process Stage 3: All products with full variant data
  useEffect(() => {
    let products: ShopifyProduct[] = [];
    
    // Try collection data first
    if (allProductsData?.collection?.products?.edges) {
      products = processProducts(allProductsData.collection.products.edges);
      debug(`STAGE 3: Processed ${products.length} products from full collection query`);
    }
    // Fallback to all products
    else if (fallbackData?.products?.edges) {
      products = processProducts(fallbackData.products.edges);
      debug(`STAGE 3: Processed ${products.length} products from fallback query`);
    }
    
    if (products.length > 0) {
      const allProductOptions = convertToProductOptions(products);
      setAllProducts(allProductOptions);
      setAllShopifyProducts(products);
      debug(`STAGE 3: All products loading complete with full variant data`);
    }
  }, [allProductsData, fallbackData]);

  return {
    // Stage 1: Initial products
    initialProducts,
    initialShopifyProducts,
    isInitialLoading,
    
    // Stage 2: Intermediate products (1 variant each)
    intermediateProducts,
    intermediateShopifyProducts,
    isIntermediateLoading: intermediateLoading,
    
    // Stage 3: All products (full variant data)
    allProducts,
    allShopifyProducts,
    isAllProductsLoading: allProductsLoading || fallbackLoading,
    
    error: initialError || intermediateError || allProductsError || fallbackError
  };
}